import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Check, X, Trash2, MapPin, ExternalLink } from "lucide-react";

export default function AdminStories() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/stories");
      setItems(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const approve = async (id, currentlyApproved) => {
    const url = currentlyApproved ? `/admin/stories/${id}/unapprove` : `/admin/stories/${id}/approve`;
    await api.put(url);
    setItems((arr) => arr.map((s) => (s.id === id ? { ...s, approved: !currentlyApproved } : s)));
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this story permanently?")) return;
    await api.delete(`/admin/stories/${id}`);
    setItems((arr) => arr.filter((s) => s.id !== id));
  };

  const pending = items.filter((s) => !s.approved).length;
  const filtered = filter === "all" ? items : filter === "pending" ? items.filter((s) => !s.approved) : items.filter((s) => s.approved);

  return (
    <div data-testid="admin-stories-page">
      <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
        <div>
          <p className="label-eyebrow mb-2">Studio · Stories</p>
          <h1 className="font-display text-4xl">Customer Stories</h1>
          <p className="text-sm text-[var(--si-text-2)] mt-1">
            {items.length} total · <span className="text-[var(--si-terracotta)] font-medium">{pending} awaiting review</span>
          </p>
        </div>
        <select className="input-field text-sm py-2" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="stories-filter">
          <option value="all">All ({items.length})</option>
          <option value="pending">Awaiting review ({pending})</option>
          <option value="approved">Approved ({items.length - pending})</option>
        </select>
      </div>

      {loading ? (
        <p className="text-[var(--si-text-2)]">Loading...</p>
      ) : filtered.length === 0 ? (
        <div className="admin-card p-12 text-center">
          <p className="text-[var(--si-text-2)]">No stories here.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((s) => (
            <div key={s.id} className={`admin-card overflow-hidden flex flex-col md:flex-row gap-0 ${!s.approved ? "border-l-4 border-l-[var(--si-terracotta)]" : ""}`} data-testid={`admin-story-${s.id}`}>
              {s.photo && (
                <img src={s.photo} alt={s.name} className="md:w-48 md:h-auto h-48 object-cover" />
              )}
              <div className="p-6 flex-1">
                <div className="flex items-start justify-between gap-4 mb-2">
                  <div>
                    <p className="font-display text-2xl">{s.name}</p>
                    <p className="text-sm text-[var(--si-text-2)] flex items-center gap-2">
                      {s.location && (<><MapPin size={12} />{s.location}</>)}
                      {s.location && s.instrument && " · "}
                      {s.instrument}
                    </p>
                    <p className="text-xs text-[var(--si-text-2)] mt-1">
                      {new Date(s.created_at).toLocaleString()} · {s.approved ? <span className="text-[var(--si-forest)]">Approved</span> : <span className="text-[var(--si-terracotta)]">Pending</span>}
                    </p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => approve(s.id, s.approved)} className={`p-2 ${s.approved ? "hover:text-[var(--si-terracotta)]" : "hover:text-[var(--si-forest)]"}`}
                      title={s.approved ? "Unapprove (hide from site)" : "Approve (show on site)"}
                      data-testid={`approve-${s.id}`}>
                      {s.approved ? <X size={16} /> : <Check size={16} />}
                    </button>
                    <button onClick={() => remove(s.id)} className="p-2 hover:text-red-700" title="Delete" data-testid={`del-story-${s.id}`}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                <p className="text-[var(--si-text)] whitespace-pre-wrap leading-relaxed border-t border-[var(--si-border)] pt-3 mt-3 italic">
                  {s.story}
                </p>
                {s.personal_link && (
                  <a href={s.personal_link.startsWith("http") ? s.personal_link : `https://${s.personal_link}`}
                     target="_blank" rel="noopener noreferrer"
                     className="inline-flex items-center gap-1 mt-3 text-sm text-[var(--si-terracotta)] hover:underline">
                    <ExternalLink size={13} /> {s.personal_link}
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
